import { useState } from 'react';
import { Plus, Trash2, Edit2, Save, X } from 'lucide-react';
import Button from '../ui/Button';
import Card, { CardBody, CardHeader } from '../ui/Card';

interface Node {
  label: string;
  category: string;
  description: string;
  properties: Property[];
}

interface Property {
  name: string;
  type: string;
  required: boolean;
  unique: boolean;
  indexed: boolean;
  description: string;
}

interface Relationship {
  from: string;
  to: string;
  type: string;
  description: string;
  cardinality?: string;
  properties?: Property[];
}

interface SchemaEditorProps {
  schema: {
    nodes: Node[];
    relationships: Relationship[];
    indexes?: any[];
    constraints?: any[];
    summary?: string;
  };
  onSave: (updatedSchema: any) => void;
  onCancel: () => void;
}

export default function SchemaEditor({ schema, onSave, onCancel }: SchemaEditorProps) {
  const [editedNodes, setEditedNodes] = useState<Node[]>(schema.nodes || []);
  const [editedRelationships, setEditedRelationships] = useState<Relationship[]>(schema.relationships || []);

  // Node editing state
  const [editingNodeIndex, setEditingNodeIndex] = useState<number | null>(null);
  const [showAddNode, setShowAddNode] = useState(false);
  const [newNode, setNewNode] = useState<Node>({
    label: '',
    category: 'Master Data',
    description: '',
    properties: []
  });

  // Relationship editing state
  const [showAddRelationship, setShowAddRelationship] = useState(false);
  const [newRelationship, setNewRelationship] = useState<Relationship>({
    from: '',
    to: '',
    type: '',
    description: '',
    cardinality: 'one-to-many'
  });

  // Property editing state
  const [editingProperty, setEditingProperty] = useState<{ nodeIndex: number; propIndex: number } | null>(null);

  const addNode = () => {
    if (newNode.label && newNode.description) {
      setEditedNodes([...editedNodes, newNode]);
      setNewNode({
        label: '',
        category: 'Master Data',
        description: '',
        properties: []
      });
      setShowAddNode(false);
    }
  };

  const addRelationship = () => {
    if (newRelationship.from && newRelationship.to && newRelationship.type) {
      setEditedRelationships([...editedRelationships, newRelationship]);
      setNewRelationship({
        from: '',
        to: '',
        type: '',
        description: '',
        cardinality: 'one-to-many'
      });
      setShowAddRelationship(false);
    }
  };

  const deleteNode = (index: number) => {
    setEditedNodes(editedNodes.filter((_, i) => i !== index));
  };

  const deleteRelationship = (index: number) => {
    setEditedRelationships(editedRelationships.filter((_, i) => i !== index));
  };

  const addPropertyToNode = (nodeIndex: number) => {
    const node = editedNodes[nodeIndex];
    const newProperty: Property = {
      name: 'newProperty',
      type: 'String',
      required: false,
      unique: false,
      indexed: false,
      description: ''
    };

    const updatedNodes = [...editedNodes];
    updatedNodes[nodeIndex] = {
      ...node,
      properties: [...(node.properties || []), newProperty]
    };
    setEditedNodes(updatedNodes);
  };

  const updateProperty = (nodeIndex: number, propIndex: number, field: keyof Property, value: any) => {
    const updatedNodes = [...editedNodes];
    const node = updatedNodes[nodeIndex];
    const updatedProperties = [...(node.properties || [])];
    updatedProperties[propIndex] = {
      ...updatedProperties[propIndex],
      [field]: value
    };
    updatedNodes[nodeIndex] = {
      ...node,
      properties: updatedProperties
    };
    setEditedNodes(updatedNodes);
  };

  const deleteProperty = (nodeIndex: number, propIndex: number) => {
    const updatedNodes = [...editedNodes];
    const node = updatedNodes[nodeIndex];
    updatedNodes[nodeIndex] = {
      ...node,
      properties: (node.properties || []).filter((_, i) => i !== propIndex)
    };
    setEditedNodes(updatedNodes);
  };

  const handleSave = () => {
    onSave({
      ...schema,
      nodes: editedNodes,
      relationships: editedRelationships
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-neutral-900">Edit Schema</h3>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={onCancel}>
            <X className="w-4 h-4" />
            Cancel
          </Button>
          <Button onClick={handleSave}>
            <Save className="w-4 h-4" />
            Save Changes
          </Button>
        </div>
      </div>

      {/* Nodes Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <h4 className="text-md font-semibold text-neutral-900">Node Types ({editedNodes.length})</h4>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowAddNode(true)}
            >
              <Plus className="w-4 h-4" />
              Add Node
            </Button>
          </div>
        </CardHeader>
        <CardBody>
          <div className="space-y-4">
            {/* Add New Node Form */}
            {showAddNode && (
              <div className="border-2 border-dashed border-primary-300 rounded-lg p-4 bg-primary-50">
                <h5 className="font-medium text-neutral-900 mb-3">Add New Node Type</h5>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">Label</label>
                    <input
                      type="text"
                      value={newNode.label}
                      onChange={(e) => setNewNode({ ...newNode, label: e.target.value })}
                      className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="e.g., Customer"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">Category</label>
                    <select
                      value={newNode.category}
                      onChange={(e) => setNewNode({ ...newNode, category: e.target.value })}
                      className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="Master Data">Master Data</option>
                      <option value="Transactional Data">Transactional Data</option>
                      <option value="Reference Data">Reference Data</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">Description</label>
                    <textarea
                      value={newNode.description}
                      onChange={(e) => setNewNode({ ...newNode, description: e.target.value })}
                      className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      rows={2}
                      placeholder="Describe what this node represents..."
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" onClick={addNode}>
                      <Plus className="w-4 h-4" />
                      Add Node
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setShowAddNode(false)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Existing Nodes */}
            {editedNodes.map((node, nodeIndex) => (
              <div key={nodeIndex} className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h5 className="font-medium text-neutral-900">{node.label}</h5>
                      <span className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded-full">
                        {node.category}
                      </span>
                    </div>
                    <p className="text-sm text-neutral-700">{node.description}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => addPropertyToNode(nodeIndex)}
                      className="p-1.5 text-purple-600 hover:bg-purple-100 rounded"
                      title="Add Property"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => deleteNode(nodeIndex)}
                      className="p-1.5 text-red-600 hover:bg-red-100 rounded"
                      title="Delete Node"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Properties */}
                {node.properties && node.properties.length > 0 && (
                  <div className="mt-3 space-y-2">
                    <p className="text-xs font-medium text-neutral-700">Properties:</p>
                    {node.properties.map((prop, propIndex) => (
                      <div key={propIndex} className="bg-white border border-purple-300 rounded p-2">
                        <div className="flex items-start gap-2">
                          <div className="flex-1 grid grid-cols-2 gap-2 text-xs">
                            <input
                              type="text"
                              value={prop.name}
                              onChange={(e) => updateProperty(nodeIndex, propIndex, 'name', e.target.value)}
                              className="px-2 py-1 border border-neutral-300 rounded"
                              placeholder="Property name"
                            />
                            <select
                              value={prop.type}
                              onChange={(e) => updateProperty(nodeIndex, propIndex, 'type', e.target.value)}
                              className="px-2 py-1 border border-neutral-300 rounded"
                            >
                              <option value="String">String</option>
                              <option value="Integer">Integer</option>
                              <option value="Float">Float</option>
                              <option value="Boolean">Boolean</option>
                              <option value="Date">Date</option>
                              <option value="DateTime">DateTime</option>
                            </select>
                            <div className="col-span-2 flex items-center gap-3">
                              <label className="flex items-center gap-1">
                                <input
                                  type="checkbox"
                                  checked={prop.required}
                                  onChange={(e) => updateProperty(nodeIndex, propIndex, 'required', e.target.checked)}
                                  className="rounded"
                                />
                                <span className="text-xs">Required</span>
                              </label>
                              <label className="flex items-center gap-1">
                                <input
                                  type="checkbox"
                                  checked={prop.unique}
                                  onChange={(e) => updateProperty(nodeIndex, propIndex, 'unique', e.target.checked)}
                                  className="rounded"
                                />
                                <span className="text-xs">Unique</span>
                              </label>
                              <label className="flex items-center gap-1">
                                <input
                                  type="checkbox"
                                  checked={prop.indexed}
                                  onChange={(e) => updateProperty(nodeIndex, propIndex, 'indexed', e.target.checked)}
                                  className="rounded"
                                />
                                <span className="text-xs">Indexed</span>
                              </label>
                            </div>
                          </div>
                          <button
                            onClick={() => deleteProperty(nodeIndex, propIndex)}
                            className="p-1 text-red-600 hover:bg-red-100 rounded"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardBody>
      </Card>

      {/* Relationships Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <h4 className="text-md font-semibold text-neutral-900">Relationships ({editedRelationships.length})</h4>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowAddRelationship(true)}
            >
              <Plus className="w-4 h-4" />
              Add Relationship
            </Button>
          </div>
        </CardHeader>
        <CardBody>
          <div className="space-y-4">
            {/* Add New Relationship Form */}
            {showAddRelationship && (
              <div className="border-2 border-dashed border-blue-300 rounded-lg p-4 bg-blue-50">
                <h5 className="font-medium text-neutral-900 mb-3">Add New Relationship</h5>
                <div className="space-y-3">
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">From Node</label>
                      <select
                        value={newRelationship.from}
                        onChange={(e) => setNewRelationship({ ...newRelationship, from: e.target.value })}
                        className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="">Select...</option>
                        {editedNodes.map((node, i) => (
                          <option key={i} value={node.label}>{node.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">Relationship Type</label>
                      <input
                        type="text"
                        value={newRelationship.type}
                        onChange={(e) => setNewRelationship({ ...newRelationship, type: e.target.value.toUpperCase().replace(/\s+/g, '_') })}
                        className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="HAS, BELONGS_TO, etc."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">To Node</label>
                      <select
                        value={newRelationship.to}
                        onChange={(e) => setNewRelationship({ ...newRelationship, to: e.target.value })}
                        className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="">Select...</option>
                        {editedNodes.map((node, i) => (
                          <option key={i} value={node.label}>{node.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">Description</label>
                    <textarea
                      value={newRelationship.description}
                      onChange={(e) => setNewRelationship({ ...newRelationship, description: e.target.value })}
                      className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      rows={2}
                      placeholder="Describe this relationship..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">Cardinality</label>
                    <select
                      value={newRelationship.cardinality}
                      onChange={(e) => setNewRelationship({ ...newRelationship, cardinality: e.target.value })}
                      className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="one-to-one">One-to-One</option>
                      <option value="one-to-many">One-to-Many</option>
                      <option value="many-to-many">Many-to-Many</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" onClick={addRelationship}>
                      <Plus className="w-4 h-4" />
                      Add Relationship
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setShowAddRelationship(false)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Existing Relationships */}
            {editedRelationships.map((rel, relIndex) => (
              <div key={relIndex} className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 flex-1">
                    <span className="font-medium text-neutral-900">({rel.from})</span>
                    <span className="text-sm px-2 py-1 bg-blue-100 text-blue-700 rounded">
                      -{rel.type}→
                    </span>
                    <span className="font-medium text-neutral-900">({rel.to})</span>
                    {rel.cardinality && (
                      <span className="text-xs text-neutral-500">({rel.cardinality})</span>
                    )}
                  </div>
                  <button
                    onClick={() => deleteRelationship(relIndex)}
                    className="p-1.5 text-red-600 hover:bg-red-100 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-sm text-neutral-700 mt-2">{rel.description}</p>
              </div>
            ))}
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
